import { useEffect, useRef, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { ExpansionPanel, InputField, DatePicker } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { CompanyAnnual } from '@/services/accountService/companyAnnualService';
import { sanitizeFormerAuditorAddress } from '@/utils/formerAuditorFields';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  disableAccordion?: boolean;
  /** qualifyOpinion === 1 时不展示 former auditor 相关字段 */
  hideFormerAuditor?: boolean;
  initialData?: {
    isChangeName?: number;
    companyName?: string | null;
    companyChineseName?: string | null;
    currentCompanyName?: string | null;
    currentCompanyChineseName?: string | null;
    oldCompanyName?: string | null;
    oldCompanyChineseName?: string | null;
    changeEffectiveDate?: string | null;
    changeOfFinancialYearEnd?: number;
    dateOfBoardResolution?: string | null;
    reasonToChange?: string | null;
    formerAuditor?: string;
    addressOfFormerAuditor?: string;
    professionalClearance?: string | null;
  } | null;
};

type LocalState = {
  isChangeName: number | undefined;
  changeEffectiveDate: string | null;
  companyName: string | null;
  companyChineseName: string | null;
  currentCompanyName: string | null;
  currentCompanyChineseName: string | null;
  oldCompanyName: string | null;
  oldCompanyChineseName: string | null;
  changeOfFinancialYearEnd: number | undefined;
  reasonToChange: string | null;
  dateOfBoardResolution: string | null;
  formerAuditor: string;
  addressOfFormerAuditor: string;
  professionalClearance: string | null;
};

type FormerAuditorFieldKey = 'formerAuditor' | 'addressOfFormerAuditor' | 'professionalClearance';

const sanitizeFormerAuditorBlock = (state: Pick<LocalState, FormerAuditorFieldKey>): Pick<LocalState, FormerAuditorFieldKey> => ({
  formerAuditor: state.formerAuditor,
  addressOfFormerAuditor: sanitizeFormerAuditorAddress(state.formerAuditor, state.addressOfFormerAuditor),
  professionalClearance: state.professionalClearance,
});

const mergeFromAnnual = (
  prev: LocalState,
  annual: Partial<Omit<CompanyAnnual, 'companyName' | 'companyChineseName'>> & {
    companyName?: string | null;
    companyChineseName?: string | null;
    currentCompanyName?: string | null;
    currentCompanyChineseName?: string | null;
    oldCompanyName?: string | null;
    oldCompanyChineseName?: string | null;
    changeEffectiveDate?: string | null;
  },
): LocalState => {
  // Use !== undefined so explicit null from API/form clears stale local (?? would keep prev)
  const pick = <T,>(incoming: T | undefined, fallback: T): T =>
    (incoming !== undefined ? incoming : fallback);

  const merged: LocalState = {
    isChangeName: pick(annual.isChangeName, prev.isChangeName),
    changeEffectiveDate: pick(annual.changeEffectiveDate, prev.changeEffectiveDate),
    companyName: pick(annual.companyName, prev.companyName),
    companyChineseName: pick(annual.companyChineseName, prev.companyChineseName),
    currentCompanyName: pick(annual.currentCompanyName, prev.currentCompanyName),
    currentCompanyChineseName: pick(annual.currentCompanyChineseName, prev.currentCompanyChineseName),
    oldCompanyName: pick(annual.oldCompanyName, prev.oldCompanyName),
    oldCompanyChineseName: pick(annual.oldCompanyChineseName, prev.oldCompanyChineseName),
    changeOfFinancialYearEnd: pick(annual.changeOfFinancialYearEnd, prev.changeOfFinancialYearEnd),
    reasonToChange: pick(annual.reasonToChange, prev.reasonToChange),
    dateOfBoardResolution: pick(annual.dateOfBoardResolution, prev.dateOfBoardResolution),
    formerAuditor: pick(annual.formerAuditor, prev.formerAuditor),
    addressOfFormerAuditor: pick(annual.addressOfFormerAuditor, prev.addressOfFormerAuditor),
    professionalClearance: pick(annual.professionalClearance, prev.professionalClearance),
  };
  return { ...merged, ...sanitizeFormerAuditorBlock(merged) };
};

const pickStoreAddress = (prev: LocalState, annual: Partial<CompanyAnnual>): string => {
  if (prev.addressOfFormerAuditor?.trim()) return prev.addressOfFormerAuditor;
  const storeAddress = (annual.addressOfFormerAuditor ?? '').trim();
  if (!storeAddress) return prev.addressOfFormerAuditor;
  const auditorName = (prev.formerAuditor?.trim() || annual.formerAuditor || '').trim();
  if (auditorName && storeAddress === auditorName) return prev.addressOfFormerAuditor;
  return annual.addressOfFormerAuditor ?? prev.addressOfFormerAuditor;
};

/** Fill only empty local fields from store (e.g. content import landed in store before form re-render). */
const mergeStoreGaps = (prev: LocalState, annual: Partial<CompanyAnnual>): LocalState => {
  const merged: LocalState = {
    ...prev,
    isChangeName: prev.isChangeName ?? annual.isChangeName,
    changeEffectiveDate: prev.changeEffectiveDate ?? annual.changeEffectiveDate ?? null,
    companyName: prev.companyName ?? annual.companyName ?? null,
    companyChineseName: prev.companyChineseName ?? annual.companyChineseName ?? null,
    currentCompanyName: prev.currentCompanyName ?? annual.currentCompanyName ?? null,
    currentCompanyChineseName: prev.currentCompanyChineseName ?? annual.currentCompanyChineseName ?? null,
    oldCompanyName: prev.oldCompanyName ?? annual.oldCompanyName ?? null,
    oldCompanyChineseName: prev.oldCompanyChineseName ?? annual.oldCompanyChineseName ?? null,
    changeOfFinancialYearEnd: prev.changeOfFinancialYearEnd ?? annual.changeOfFinancialYearEnd,
    reasonToChange: prev.reasonToChange ?? annual.reasonToChange ?? null,
    dateOfBoardResolution: prev.dateOfBoardResolution ?? annual.dateOfBoardResolution ?? null,
    formerAuditor: prev.formerAuditor?.trim() ? prev.formerAuditor : (annual.formerAuditor ?? prev.formerAuditor),
    addressOfFormerAuditor: pickStoreAddress(prev, annual),
    professionalClearance: prev.professionalClearance?.trim()
      ? prev.professionalClearance
      : (annual.professionalClearance ?? prev.professionalClearance),
  };
  return { ...merged, ...sanitizeFormerAuditorBlock(merged) };
};

const isSameLocal = (a: LocalState, b: LocalState) =>
  a.isChangeName === b.isChangeName
  && a.changeEffectiveDate === b.changeEffectiveDate
  && a.companyName === b.companyName
  && a.companyChineseName === b.companyChineseName
  && a.currentCompanyName === b.currentCompanyName
  && a.currentCompanyChineseName === b.currentCompanyChineseName
  && a.oldCompanyName === b.oldCompanyName
  && a.oldCompanyChineseName === b.oldCompanyChineseName
  && a.changeOfFinancialYearEnd === b.changeOfFinancialYearEnd
  && a.reasonToChange === b.reasonToChange
  && a.dateOfBoardResolution === b.dateOfBoardResolution
  && a.formerAuditor === b.formerAuditor
  && a.addressOfFormerAuditor === b.addressOfFormerAuditor
  && a.professionalClearance === b.professionalClearance;

const hasFormerAuditorInfo = (state: Pick<LocalState, 'formerAuditor' | 'addressOfFormerAuditor' | 'professionalClearance'>) =>
  Boolean(state.formerAuditor?.trim() || state.addressOfFormerAuditor?.trim() || state.professionalClearance?.trim());

export default function EpCompanyNameAuditorChanges({ annualId, onChange, disableAccordion = false, hideFormerAuditor = false, initialData }: Props) {
  const getCompanyAndAuditorChangesInfo = useCompanyAnnualStore(s => s.getCompanyAndAuditorChangesInfo);
  const storeAnnual = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);
  const userEditedRef = useRef<Set<FormerAuditorFieldKey>>(new Set());
  const nameChangeDraftRef = useRef<{
    changeEffectiveDate: string | null;
    oldCompanyName: string | null;
    oldCompanyChineseName: string | null;
    companyName: string | null;
    companyChineseName: string | null;
  } | null>(null);
  const [local, setLocal] = useState<LocalState>({
    isChangeName: undefined as number | undefined,
    changeEffectiveDate: null as string | null,
    companyName: null as string | null,
    companyChineseName: null as string | null,
    currentCompanyName: null as string | null,
    currentCompanyChineseName: null as string | null,
    oldCompanyName: null as string | null,
    oldCompanyChineseName: null as string | null,
    changeOfFinancialYearEnd: undefined as number | undefined,
    reasonToChange: null as string | null,
    dateOfBoardResolution: null as string | null,
    formerAuditor: '',
    addressOfFormerAuditor: '',
    professionalClearance: null as string | null,
  });

  useEffect(() => {
    userEditedRef.current.clear();
    nameChangeDraftRef.current = null;
  }, [annualId]);

  const applyUserEdits = (prev: LocalState, next: LocalState): LocalState => {
    const edited = userEditedRef.current;
    if (edited.size === 0) return next;
    return {
      ...next,
      formerAuditor: edited.has('formerAuditor') ? prev.formerAuditor : next.formerAuditor,
      addressOfFormerAuditor: edited.has('addressOfFormerAuditor') ? prev.addressOfFormerAuditor : next.addressOfFormerAuditor,
      professionalClearance: edited.has('professionalClearance') ? prev.professionalClearance : next.professionalClearance,
    };
  };

  useEffect(() => {
    if (initialData || storeAnnual) {
      setLocal(prev => {
        let next = prev;
        if (storeAnnual) {
          next = initialData ? mergeStoreGaps(prev, storeAnnual) : mergeFromAnnual(prev, storeAnnual);
        }
        if (initialData) {
          next = mergeFromAnnual(next, initialData);
        }
        next = applyUserEdits(prev, next);
        return isSameLocal(prev, next) ? prev : next;
      });
      return;
    }

    let mounted = true;
    (async () => {
      if (!annualId) return;
      const v = await getCompanyAndAuditorChangesInfo(annualId);
      if (!mounted) return;
      setLocal(prev => ({
        ...prev,
        ...v,
        companyName: (v as LocalState).companyName ?? prev.companyName,
        companyChineseName: (v as LocalState).companyChineseName ?? prev.companyChineseName,
        currentCompanyName: (v as LocalState).currentCompanyName ?? prev.currentCompanyName,
        currentCompanyChineseName: (v as LocalState).currentCompanyChineseName ?? prev.currentCompanyChineseName,
      }));
    })();
    return () => { mounted = false; };
  }, [annualId, getCompanyAndAuditorChangesInfo, initialData, storeAnnual]);

  const pickNonEmptyName = (...candidates: Array<string | null | undefined>): string | null => {
    for (const c of candidates) {
      const t = (c ?? '').trim();
      if (t) return t;
    }
    return null;
  };

  const emit = (patch: Partial<LocalState>) => {
    (Object.keys(patch) as (keyof LocalState)[]).forEach((key) => {
      if (key === 'formerAuditor' || key === 'addressOfFormerAuditor' || key === 'professionalClearance') {
        userEditedRef.current.add(key);
      }
    });
    setLocal(prev => {
      const merged = { ...prev, ...patch };
      const next = { ...merged, ...sanitizeFormerAuditorBlock(merged) };
      const outbound: Partial<CompanyAnnual> = { ...(patch as Partial<CompanyAnnual>) };
      if (
        !userEditedRef.current.has('addressOfFormerAuditor')
        && next.addressOfFormerAuditor !== merged.addressOfFormerAuditor
      ) {
        outbound.addressOfFormerAuditor = next.addressOfFormerAuditor;
      }
      // Keep store in sync so save payload / re-merge do not drop name-change fields
      const id = Number(annualId);
      if (id > 0) {
        const existing = useCompanyAnnualStore.getState().byAnnualId[id];
        useCompanyAnnualStore.getState().setAnnual({
          ...(existing || ({ annualId: id } as CompanyAnnual)),
          annualId: id,
          ...outbound,
        } as CompanyAnnual);
      }
      onChange?.(outbound);
      return next;
    });
  };

  const onIsChangeNameChange = (raw: string) => {
    if (raw === '') {
      emit({ isChangeName: undefined as unknown as number });
      return;
    }
    const nextFlag = Number(raw);
    const prevFlag = Number(local.isChangeName ?? 0);
    if (prevFlag === nextFlag) {
      emit({ isChangeName: nextFlag });
      return;
    }

    if (prevFlag !== 1 && nextFlag === 1) {
      // No → Yes: old = period-resolved current* (fallback latest companyName before clear)
      const draft = nameChangeDraftRef.current;
      const oldEn = pickNonEmptyName(
        draft?.oldCompanyName,
        local.currentCompanyName,
        local.companyName,
      );
      const oldZh = pickNonEmptyName(
        draft?.oldCompanyChineseName,
        local.currentCompanyChineseName,
        local.companyChineseName,
      );
      const patch: Partial<LocalState> = {
        isChangeName: 1,
        oldCompanyName: oldEn,
        oldCompanyChineseName: oldZh,
        companyName: draft?.companyName ?? '',
        companyChineseName: draft?.companyChineseName ?? '',
        changeEffectiveDate: draft?.changeEffectiveDate ?? null,
      };
      emit(patch);
      return;
    }

    if (prevFlag === 1 && nextFlag === 0) {
      // Yes → No: restore names from old*; clear change fields before save
      nameChangeDraftRef.current = {
        changeEffectiveDate: local.changeEffectiveDate,
        oldCompanyName: local.oldCompanyName,
        oldCompanyChineseName: local.oldCompanyChineseName,
        companyName: local.companyName,
        companyChineseName: local.companyChineseName,
      };
      emit({
        isChangeName: 0,
        companyName: local.oldCompanyName,
        companyChineseName: local.oldCompanyChineseName,
        changeEffectiveDate: null,
        oldCompanyName: null,
        oldCompanyChineseName: null,
      });
      return;
    }

    emit({ isChangeName: nextFlag });
  };

  return (
    <ExpansionPanel 
      title="Company Name/Auditor Changes (公司名稱/審計師變更)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <Grid container spacing={0.5}>
        <Grid size={6 as any}>
          <InputField
            select
            fullWidth
            variant="outlined"
            label="Change of Co. name (是否改公司名称?)"
            value={local.isChangeName ?? ''}
            onChange={(e) => onIsChangeNameChange(String((e.target as HTMLInputElement).value))}
          >
            <MenuItem dense value="">Select</MenuItem>
            <MenuItem dense value={0}>No</MenuItem>
            <MenuItem dense value={1}>Yes</MenuItem>
          </InputField>
        </Grid>
        {Number(local.isChangeName) === 1 ? (
          <>
            <Grid size={6 as any}>
              <DatePicker fullWidth variant="outlined" label="Since when? (改變生效日期)" value={local.changeEffectiveDate || null} onChange={(v) => emit({ changeEffectiveDate: v || null })} />
            </Grid>
            <Grid size={12 as any}>
              <InputField
                fullWidth
                variant="outlined"
                label="Company Name (公司名稱)"
                value={local.companyName || ''}
                onChange={(e) => emit({ companyName: e.target.value || null })}
              />
            </Grid>
            <Grid size={12 as any}>
              <InputField
                fullWidth
                variant="outlined"
                label="Company Chinese Name (公司中文名稱)"
                value={local.companyChineseName || ''}
                onChange={(e) => emit({ companyChineseName: e.target.value || null })}
              />
            </Grid>
            <Grid size={12 as any}>
              <InputField
                fullWidth
                variant="outlined"
                label="Old Co. Name (舊公司名稱)"
                value={local.oldCompanyName || ''}
                display
              />
            </Grid>
            <Grid size={12 as any}>
              <InputField
                fullWidth
                variant="outlined"
                label="Old Co. Chinese Name (舊公司中文名稱)"
                value={local.oldCompanyChineseName || ''}
                display
              />
            </Grid>
          </>
        ) : (
          <Grid size={6 as any}></Grid>
        )}
        <Grid size={6 as any}>
          <InputField select fullWidth variant="outlined" label="Change of financial year end? (有否改會計年結日?)" value={local.changeOfFinancialYearEnd ?? ''} onChange={(e) => emit({ changeOfFinancialYearEnd: Number(e.target.value) as any })}>
            <MenuItem dense value="">Select</MenuItem>
            <MenuItem dense value={0}>No</MenuItem>
            <MenuItem dense value={1}>Yes</MenuItem>
          </InputField>
        </Grid>
        {local.changeOfFinancialYearEnd === 1 ? (
          <Grid size={12 as any}>
            <InputField fullWidth variant="outlined" label="Reason to change? (改變的原因?)" value={local.reasonToChange || ''} onChange={(e) => emit({ reasonToChange: e.target.value || null })} />
          </Grid>
        ) : (
          <Grid size={6 as any}></Grid>
        )}
        {local.changeOfFinancialYearEnd !== 0 && (
          <Grid size={6 as any}>
            <DatePicker fullWidth variant="outlined" label="Date of board resolution? (董事局議程的日期?)" value={local.dateOfBoardResolution || null} onChange={(v) => emit({ dateOfBoardResolution: v || null })} />
          </Grid>
        )}
        {!hideFormerAuditor && (
          <>
            <Grid size={12 as any}>
              <InputField fullWidth variant="outlined" label="Name of former auditor (上手會計師名稱)" value={local.formerAuditor} onChange={(e) => emit({ formerAuditor: e.target.value })} />
            </Grid>
            {hasFormerAuditorInfo(local) && (
              <>
                <Grid size={12 as any}>
                  <InputField fullWidth variant="outlined" label="Former Auditor address line 1" value={local.addressOfFormerAuditor} onChange={(e) => emit({ addressOfFormerAuditor: e.target.value })} />
                </Grid>
                <Grid size={12 as any}>
                  <InputField fullWidth variant="outlined" label="Former Auditor address line 2" value={local.professionalClearance || ''} onChange={(e) => emit({ professionalClearance: e.target.value || null })} />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </ExpansionPanel>
  );
}
